import styled from "@emotion/styled";
import { withIsVisible } from "../../../utils/reactUtils";
import React, { useMemo, useState } from "react";
import { Text } from "../../general/Text";
import { TokenList } from "../../../store/global/getAllTokenLists";
import { Address, Hash } from "viem";
import { PercentageChange } from "./TokenRowStats";
import { TokenListMetadata } from "../../../hooks/queries/tokens-queries";
import { Chain } from "wagmi";
import { formatAddress } from "../../../utils/walletUtils";
import GlobeIcon from "../../../img/token_selector/Globe.svg?react";
import CoinGeckoIcon from "../../../img/token_selector/Coingecko.svg?react";
import XIcon from "../../../img/token_selector/X.svg?react";
import ViewBlock from "../../../img/butter_svgs/ViewBlock.svg?react";
import { GlobalStore } from "../../../store/global/GlobalStore";
import { Button } from "../../general/Button/Button";
import { css } from "@emotion/react";
import { NATIVE_TOKEN_ADDRESS } from "../../../config/consts";
import { openAddressInBlockExplorer } from "../../NavbarV2/elements/ProfileActions/elements/ProfileActionsContentWithConnection/elements/ProfileActionsOptions";
import { formatMetadataValues } from "../../../utils/tokenSelectorUtils";
import { TokenSelectorPreviewChart } from "./TokenSelectorPreviewChart";
import { CopyButton } from "../../CopyButton/CopyButton";
import { Amount } from "../../../types/amount";
import { COINGECKO_NATIVE_TOKEN_ADDRESS } from "../../../utils/coinGeckoUtils";
import { isNativeToken } from "../../../utils/wrapUtils";
import { getTokenIcon } from "../../../utils/tokenIconUtils";

interface TokenSelectorPreviewProps {
  tokenAddress: Address;
  tokenList: TokenList | undefined;
  chain: Chain;
  isLoading: boolean;
  tokenListMetadata: TokenListMetadata | undefined;
}

export const TokenSelectorPreview = withIsVisible(
  ({ tokenAddress, tokenList, chain, isLoading, tokenListMetadata }: TokenSelectorPreviewProps) => {
    const [isCopied, setIsCopied] = useState(false);
    const theme = GlobalStore.useState(s => s.theme);
    const coinGeckoTokenList = GlobalStore.useState(s => s.coinGeckoTokenList);

    const percentChange = tokenListMetadata?.[tokenAddress]?.c ?? 0;

    const { homepageLink, twitterLink, coinGeckoLink, coinGeckoData } = useMemo(() => {
      const coinGeckoData =
        coinGeckoTokenList?.[
          isNativeToken(tokenAddress, chain)
            ? COINGECKO_NATIVE_TOKEN_ADDRESS
            : (tokenAddress.toLowerCase() as Hash)
        ];
      const homepageLink = coinGeckoData?.homepage;
      const twitterLink = `https://twitter.com/${coinGeckoData?.twitterScreenName}`;
      const coinGeckoLink = `https://www.coingecko.com/en/coins/${coinGeckoData?.id}`;
      return { homepageLink, twitterLink, coinGeckoLink, coinGeckoData };
    }, [coinGeckoTokenList, tokenAddress, chain]);

    const showSocialLinks = homepageLink || coinGeckoData?.twitterScreenName || coinGeckoData?.id;
    const showMetaData = tokenListMetadata?.[tokenAddress] != null;
    const tokenMetadata = tokenListMetadata?.[tokenAddress];
    const volume = formatMetadataValues(tokenMetadata?.v);
    const fdv = formatMetadataValues(tokenMetadata?.f);
    const marketCap = formatMetadataValues(tokenMetadata?.m);
    const rawPrice = tokenMetadata?.p?.toString() ?? "0";
    const priceUsd =
      rawPrice === "0"
        ? "-"
        : new Amount(rawPrice).toFormattedSubscript(true) ??
          `$${new Amount(rawPrice).toDisplay(tokenAddress, 2)}`;

    return (
      <div key={tokenAddress}>
        <TokenSelectorPreviewContainer>
          {" "}
          <Container>
            <img
              src={getTokenIcon(tokenAddress, tokenList)}
              alt={`${tokenAddress} icon`}
              style={{
                width: 38,
                height: 38,
                borderRadius: "50%",
              }}
            />
            <NameAndTickerAndPriceContainer>
              <NameAndTickerAndPriceSubContainer>
                <Text textStyle="Title" size="XXS" shouldScale={true}>
                  {tokenList?.[tokenAddress]?.name}
                </Text>{" "}
                <Text
                  textStyle="Paragraph"
                  size="Medium"
                  shouldScale={true}
                  color={theme.font.secondary.default}
                >
                  {tokenList?.[tokenAddress].ticker}
                </Text>
              </NameAndTickerAndPriceSubContainer>
              <PriceAndPercentageChangeContainer>
                <Text
                  textStyle={"Title"}
                  size={"Mid"}
                  textCss={css`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                  `}
                >
                  {priceUsd}
                  {percentChange != null && showMetaData && (
                    <PercentageChange
                      isLoading={isLoading}
                      pctChange={percentChange}
                      theme={theme}
                      styles={css`
                        padding-top: 4px;
                      `}
                    />
                  )}
                </Text>
              </PriceAndPercentageChangeContainer>
            </NameAndTickerAndPriceContainer>
          </Container>
          <TokenSelectorPreviewChart tokenAddress={tokenAddress} />
          <SecondContainer>
            <VolumeAndMarketCapAndFdvContainer>
              <Text textStyle={"Title"} size={"Small"}>
                {volume}
              </Text>
              <Text textStyle={"Paragraph"} size={"Medium"} color={theme.font.secondary.default}>
                24 Hr Vol
              </Text>
            </VolumeAndMarketCapAndFdvContainer>
            <VolumeAndMarketCapAndFdvContainer>
              <Text textStyle={"Title"} size={"Small"}>
                {marketCap}
              </Text>
              <Text textStyle={"Paragraph"} size={"Medium"} color={theme.font.secondary.default}>
                Market Cap
              </Text>
            </VolumeAndMarketCapAndFdvContainer>
            <VolumeAndMarketCapAndFdvContainer>
              <Text textStyle={"Title"} size={"Small"}>
                {fdv}
              </Text>
              <Text textStyle={"Paragraph"} size={"Medium"} color={theme.font.secondary.default}>
                FDV
              </Text>
            </VolumeAndMarketCapAndFdvContainer>
          </SecondContainer>
          {tokenAddress !== NATIVE_TOKEN_ADDRESS && (
            <ContractContainer>
              <Text textStyle={"Paragraph"} size={"Medium"} color={theme.font.secondary.default}>
                Contract
              </Text>
              <div
                css={css`
                  display: flex;
                  gap: 4px;
                `}
              >
                <Button
                  buttonType={"quaternary"}
                  buttonCss={css`
                    padding: 4px 13px 4px 4px;
                    justify-content: center;
                    align-items: center;
                    gap: 4px;
                    height: 24px;
                  `}
                  onClick={() => openAddressInBlockExplorer(chain, tokenAddress)}
                >
                  <IconWrapper
                    css={css`
                      padding: 0;
                    `}
                  >
                    <ViewBlock />
                  </IconWrapper>
                  <Text textStyle={"Paragraph"} size={"Large"}>
                    {formatAddress(tokenAddress)}
                  </Text>
                </Button>
                <CopyButton
                  isCopied={isCopied}
                  setIsCopied={setIsCopied}
                  copyValue={tokenAddress}
                  buttonCss={css`
                    padding: 6px;
                    background: ${theme.button["quaternary"].surface.default};
                    border-radius: 16px;
                    height: 24px;
                  `}
                  buttonType={"quaternary"}
                  iconSize={20}
                />
              </div>
            </ContractContainer>
          )}
          {showSocialLinks && (
            <LinksContainer>
              <Text textStyle={"Paragraph"} size={"Medium"} color={theme.font.secondary.default}>
                Links
              </Text>
              <SocialsContainer>
                {homepageLink && (
                  <SocialsButton
                    buttonType={"tertiary"}
                    buttonCss={css`
                      padding: 6px;
                      gap: 2px;
                    `}
                    navigateTo={homepageLink}
                  >
                    <GlobeIcon />
                  </SocialsButton>
                )}
                {coinGeckoData?.twitterScreenName && (
                  <SocialsButton
                    buttonType={"tertiary"}
                    buttonCss={css`
                      padding: 6px;
                      gap: 2px;
                    `}
                    navigateTo={twitterLink}
                  >
                    <XIcon />
                  </SocialsButton>
                )}
                {coinGeckoData?.id && (
                  <SocialsButton
                    buttonType={"quaternary"}
                    buttonCss={css`
                      padding: 4px 13px 4px 4px;
                      gap: 4px;
                    `}
                    navigateTo={coinGeckoLink}
                  >
                    <IconWrapper>
                      <CoinGeckoIcon />
                    </IconWrapper>
                    <Text textStyle={"Button"} size={"Small"}>
                      CoinGecko
                    </Text>
                  </SocialsButton>
                )}
              </SocialsContainer>
            </LinksContainer>
          )}
        </TokenSelectorPreviewContainer>
      </div>
    );
  }
);

const Container = styled.div`
  display: flex;
  padding-bottom: 12px;
  align-items: center;
  gap: 14px;
  align-self: stretch;
`;
const IconWrapper = styled.div`
  display: flex;
  width: 24px;
  height: 24px;
  padding: 4px;
  justify-content: center;
  align-items: center;
`;

const SecondContainer = styled.div`
  display: flex;
  padding: 4px 0px 12px 0px;
  justify-content: center;
  align-items: stretch;
  align-content: stretch;
  gap: 35px;
  align-self: stretch;
  flex-wrap: wrap;
`;

const NameAndTickerAndPriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: -2px;
  flex: 1 0 0;
`;
const NameAndTickerAndPriceSubContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;
const PriceAndPercentageChangeContainer = styled.div`
  display: flex;
  align-items: end;
  gap: 8px;
  align-self: stretch;
`;

const VolumeAndMarketCapAndFdvContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1 0 0;
  justify-content: space-between;
`;

const ContractContainer = styled.div`
  display: flex;
  padding: 4px 0px;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  justify-content: space-between;
`;

const LinksContainer = styled.div`
  display: flex;
  padding: 5px 0px;
  align-items: center;
  align-content: center;
  row-gap: 4px;
  align-self: stretch;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: space-between;
`;

const SocialsContainer = styled.div`
  display: flex;
  gap: 4px;
`;
const SocialsButton = styled(Button)`
  height: 24px;
  border-radius: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const TokenSelectorPreviewContainer = styled.div`
  left: 98%;
  z-index: 191919191;
  background: ${props => props.theme.button.selector.surface.hover};
  width: 290px;
  padding: 16px 16px 14px 16px;
  border-radius: 20px;
  display: flex;
  gap: 3px;
  flex-direction: column;
  box-shadow:
    0px 0px 8px 0px rgba(0, 0, 0, 0.5),
    0px 1px 15px 0px rgba(0, 0, 0, 0.25);
`;
